/**
 * ShortText display masks (QA D-21 — the RDSP internal account number reads `123-456789`).
 *
 * A mask like `###-######` is PRESENTATION ONLY: every `#` is one digit slot, every other
 * character a literal the input shows while the advisor types. The value that reaches the values
 * map, the snapshot and the PDFs stays the RAW DIGITS.
 *
 * That is deliberately the opposite of the sin / phone / postal rules in `fieldValidation.ts`,
 * which store their formatted string. Storing `123-456789` would hand a PDF comb box sized for
 * nine digits a ten-character string — and a comb given a longer string prints BLANK rather than
 * truncating (the masked-value overflow trap this codebase has already hit once).
 */

/** How many digits a complete value holds — one per `#`. */
export function maskDigitSlots(mask: string): number {
  let n = 0;
  for (const c of mask) if (c === '#') n += 1;
  return n;
}

/** What an edit leaves in state: its digits, capped at the mask's slot count. */
export function maskedDigits(mask: string, typed: string): string {
  return typed.replace(/\D/g, '').slice(0, maskDigitSlots(mask));
}

/**
 * The digits laid into the mask for display. Literals are emitted only up to the last digit
 * placed, so a partial `123` shows as `123` rather than `123-`: a trailing separator would be
 * deleted by Backspace and re-added by the next render, trapping the caret.
 */
export function formatWithMask(mask: string, digits: string): string {
  if (!digits) return '';
  let out = '';
  let pending = '';
  let i = 0;
  for (const c of mask) {
    if (i >= digits.length) break;
    if (c === '#') {
      out += pending + digits[i];
      pending = '';
      i += 1;
    } else {
      pending += c;
    }
  }
  return out;
}

/** A non-empty value is complete when it fills every slot. Empty is `required`'s business. */
export function isMaskIncomplete(mask: string, value: unknown): boolean {
  if (typeof value !== 'string' || value === '') return false;
  return value.replace(/\D/g, '').length !== maskDigitSlots(mask);
}
